import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiPieChart } from 'react-icons/fi';
const fmt  = (n) => Number(n || 0).toLocaleString('en-US');
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} MT`;

// Visual config per J/P/L category.
const STYLES = {
  J: { label: 'Signed contract (J)', bar: '#10B981', chip: 'tw-bg-emerald-100 tw-text-emerald-700' },
  P: { label: 'Prospective projects (P)', bar: '#0EA5E9', chip: 'tw-bg-sky-100 tw-text-sky-700' },
  L: { label: 'Lost jobs (L)', bar: '#EF4444', chip: 'tw-bg-red-100 tw-text-red-600' },
};

// Job-entry classification breakdown (Signed J / Prospective P / Lost L) with
// count, weight and share. Clicking a row drills into that category's records.
// `extraDrill` is merged into the drill params (e.g. { type_of_sales }) so a
// per-type chart narrows the drill-down to that Domestic/Oversea slice.
const JobEntryCategoryChart = ({ data, loading, error, onDrillDown, title, extraDrill }) => {
  const rows = data || [];
  const maxCount = Math.max(...rows.map(r => r.count || 0), 1);

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm tw-h-full">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
        <FiPieChart size={15} className="tw-text-violet-500" />
        <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">{title}</h3>
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
      ) : error || rows.length === 0 ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10 tw-mb-0">No data for this period</p>
      ) : (
        <div className="tw-p-4 tw-space-y-3">
          {rows.map((row) => {
            const style = STYLES[row.category] || STYLES.J;
            const pct   = maxCount > 0 ? ((row.count || 0) / maxCount) * 100 : 0;
            return (
              <div key={row.category}
                onClick={() => onDrillDown && onDrillDown(
                  `${style.label}${extraDrill.type_of_sales ? ` · ${extraDrill.type_of_sales}` : ''}`,
                  { category: row.category, ...extraDrill }
                )}
                className={`tw-rounded tw-px-3 tw-py-2.5 tw-transition-colors ${onDrillDown ? 'tw-cursor-pointer hover:tw-bg-gray-50' : ''}`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5 tw-gap-2">
                  <span className={`tw-inline-flex tw-items-center tw-text-xs tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-xl ${style.chip}`}>
                    {style.label}
                  </span>
                  <span className="tw-text-xs tw-text-gray-400">{row.rate || 0}%</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                  <div className="tw-flex-1 tw-h-3 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden">
                    <div className="tw-h-full tw-rounded-xl tw-transition-all tw-duration-500"
                      style={{ width: `${pct}%`, background: style.bar, opacity: 0.9 }} />
                  </div>
                  <span className="tw-text-sm tw-font-bold tw-tabular-nums tw-text-gray-900 tw-w-14 tw-text-right">{fmt(row.count)}</span>
                  <span className="tw-text-xs tw-tabular-nums tw-text-gray-500 tw-w-24 tw-text-right">{fmtW(row.weight)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

JobEntryCategoryChart.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.any,
  onDrillDown: PropTypes.func,
  title: PropTypes.string,
  extraDrill: PropTypes.object,
};
JobEntryCategoryChart.defaultProps = {
  data: [], loading: false, error: null, onDrillDown: null,
  title: 'Classification (J / P / L)', extraDrill: {},
};

export default JobEntryCategoryChart;
